/**
 * SRE Investigation API client (Phase A).
 *
 * Live mode talks to the Pulse BFF (`/api/v1/sre`) by default, which proxies
 * **report-only** agent paths (investigate SSE, ticket history, cancel).
 * Skills / use-case admin lives in Pulse-SRE FE — do not call those APIs here.
 * Set REACT_APP_SRE_USE_BFF=false to hit the agent URL directly.
 * Demo mode simulates an OpenSRE-style investigation with a full Execution Trace.
 */

import type {
  InvestigationReportData,
  InvestigationThought,
  InvestigationToolCall,
  PostInvestigationNoteRequest,
  PostInvestigationNoteResult,
  StartInvestigationRequest,
  StoredInvestigationRun,
  TicketEntityType,
} from '../types';
import { getSreInvestigationApiUrl, getSreInvestigationMode } from '../constants';
import {
  SAMPLE_CONCLUSION_MARKDOWN,
  SAMPLE_INVESTIGATION_REPORT,
} from '../fixtures/sampleInvestigationReport';
import { formatReportAsMarkdown } from '../utils/formatReportAsMarkdown';

const NEO4J_MISROUTE_MESSAGE =
  'Investigation API reached Neo4j instead of the Pulse-SRE agent. Check SRE_AGENT_URL (agent port 8001, not Neo4j 7474).';

function formatSreHttpError(text: string, fallback: string): string {
  const trimmed = (text || '').trim();
  if (!trimmed) return fallback;
  try {
    const parsed = JSON.parse(trimmed) as {
      errors?: Array<{ code?: string }>;
      message?: string;
      detail?: string;
    };
    const neo = (parsed.errors || []).some(
      (err) => typeof err?.code === 'string' && err.code.startsWith('Neo.ClientError')
    );
    if (neo) return NEO4J_MISROUTE_MESSAGE;
    if (typeof parsed.message === 'string' && parsed.message.trim()) {
      return parsed.message.trim();
    }
    if (typeof parsed.detail === 'string' && parsed.detail.trim()) {
      return parsed.detail.trim();
    }
  } catch {
    if (trimmed.includes('Neo.ClientError')) return NEO4J_MISROUTE_MESSAGE;
  }
  return trimmed;
}

function authHeaders(): HeadersInit {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };
  const token = localStorage.getItem('accessToken');
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

export function createInvestigationRunId(): string {
  return `inv_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export async function startLiveInvestigationStream(
  request: StartInvestigationRequest,
  signal?: AbortSignal
): Promise<Response> {
  const base = getSreInvestigationApiUrl().replace(/\/$/, '');
  if (!base) {
    throw new Error('SRE investigation API URL is not configured');
  }

    const response = await fetch(`${base}/investigate`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      ticket_number: request.ticketNumber,
      entity_type: request.entityType,
      entity_id: request.entityId,
      alert_id: request.alertId,
      engagement_id: request.engagementId,
      title: request.title,
      description: request.description,
      node: request.node || request.computer,
      computer: request.computer || request.node,
      resource_id: request.resourceId,
      resource_group: request.resourceGroup,
      vm_name: request.vmName,
      run_id: request.runId,
      prompt:
        request.prompt ||
        [
          `Investigate Pulse ${request.entityType || 'ticket'} ${request.ticketNumber}`,
          request.title,
          request.description,
          request.node || request.computer
            ? `Host/node: ${request.node || request.computer}`
            : '',
          request.resourceId ? `Resource: ${request.resourceId}` : '',
        ]
          .filter(Boolean)
          .join('\n'),
      trigger_source: request.triggerSource || 'pulse_ui',
      source: request.source || undefined,
      monitoring_source: request.source || undefined,
    }),
    signal,
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(formatSreHttpError(text, `Investigation failed (${response.status})`));
  }

  return response;
}

/** Cooperative cancel — sets DB flag so any agent replica stops between steps. */
export async function cancelInvestigation(runId: string): Promise<void> {
  const base = getSreInvestigationApiUrl().replace(/\/$/, '');
  if (!base || !runId.trim()) return;

  const response = await fetch(
    `${base}/investigations/${encodeURIComponent(runId)}/cancel`,
    {
      method: 'POST',
      headers: authHeaders(),
    }
  );

  if (!response.ok && response.status !== 404) {
    const text = await response.text().catch(() => '');
    throw new Error(text || `Cancel failed (${response.status})`);
  }
}

export interface ListInvestigationsParams {
  ticketNumber?: string;
  entityId?: string;
  status?: string;
  limit?: number;
}

/** List persisted runs from pulse-sre (via BFF when configured). */
export async function listInvestigations(
  params: ListInvestigationsParams = {},
  signal?: AbortSignal
): Promise<StoredInvestigationRun[]> {
  const base = getSreInvestigationApiUrl().replace(/\/$/, '');
  if (!base) {
    return [];
  }

  const qs = new URLSearchParams();
  if (params.ticketNumber?.trim()) {
    qs.set('ticket_number', params.ticketNumber.trim());
  }
  if (params.entityId?.trim()) {
    qs.set('entity_id', params.entityId.trim());
  }
  if (params.status?.trim()) {
    qs.set('status', params.status.trim());
  }
  if (params.limit != null) {
    qs.set('limit', String(params.limit));
  }

  const url = `${base}/investigations${qs.toString() ? `?${qs}` : ''}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders(),
    signal,
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(formatSreHttpError(text, `Failed to list investigations (${response.status})`));
  }

  const data: unknown = await response.json();
  if (!Array.isArray(data)) {
    return [];
  }
  return data as StoredInvestigationRun[];
}

/** Load a single persisted run (includes toolCalls + report). */
export async function getInvestigation(
  runId: string,
  signal?: AbortSignal
): Promise<StoredInvestigationRun> {
  const base = getSreInvestigationApiUrl().replace(/\/$/, '');
  if (!base) {
    throw new Error('SRE investigation API URL is not configured');
  }
  if (!runId.trim()) {
    throw new Error('Investigation run id is required');
  }

  const response = await fetch(
    `${base}/investigations/${encodeURIComponent(runId.trim())}`,
    {
      method: 'GET',
      headers: authHeaders(),
      signal,
    }
  );

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(formatSreHttpError(text, `Failed to load investigation (${response.status})`));
  }

  return (await response.json()) as StoredInvestigationRun;
}

/** Map stored API tool rows into ExecutionTrace-compatible tool calls. */
export function mapStoredToolCalls(
  run: StoredInvestigationRun
): InvestigationToolCall[] {
  const rows = run.toolCalls || [];
  return rows.map((t) => {
    const rawStatus = (t.status || 'completed').toLowerCase();
    let status: InvestigationToolCall['status'] = 'completed';
    if (rawStatus === 'running' || rawStatus === 'started') status = 'running';
    else if (rawStatus === 'error' || rawStatus === 'failed') status = 'error';

    const startedAt = t.startedAt ? new Date(t.startedAt) : new Date();
    return {
      id: t.id,
      name: t.name,
      status,
      agentName: t.agentName || undefined,
      parentAgent: t.parentAgent || undefined,
      sequenceNumber: t.sequenceNumber ?? undefined,
      input: t.input || undefined,
      output: t.output || undefined,
      errorMessage: t.errorMessage || undefined,
      startedAt,
      durationMs: t.durationMs ?? undefined,
      completedAt:
        t.durationMs != null
          ? new Date(startedAt.getTime() + t.durationMs)
          : undefined,
    };
  });
}

export function hasCompletedInvestigation(
  runs: StoredInvestigationRun[]
): boolean {
  return runs.some((r) => (r.status || '').toLowerCase() === 'completed');
}

export function latestInvestigation(
  runs: StoredInvestigationRun[]
): StoredInvestigationRun | null {
  if (!runs.length) return null;
  return [...runs].sort((a, b) => {
    const ta = Date.parse(a.startedAt || a.completedAt || '') || 0;
    const tb = Date.parse(b.startedAt || b.completedAt || '') || 0;
    return tb - ta;
  })[0];
}

/** Prefer the newest completed run for badges / summaries. */
export function latestCompletedInvestigation(
  runs: StoredInvestigationRun[]
): StoredInvestigationRun | null {
  return latestInvestigation(
    runs.filter((r) => (r.status || '').toLowerCase() === 'completed')
  );
}

export interface DemoToolStartPayload {
  id: string;
  name: string;
  agentName: string;
  sequenceNumber: number;
  input?: Record<string, unknown>;
  parentAgent?: string;
}

export interface DemoStreamHandlers {
  onThought: (thought: InvestigationThought) => void;
  onToolStart: (tool: DemoToolStartPayload) => void;
  onToolEnd: (
    id: string,
    output: string,
    options?: { success?: boolean; errorMessage?: string; durationMs?: number }
  ) => void;
  onReport: (report: InvestigationReportData, markdown: string) => void;
  onError: (message: string) => void;
}

type DemoStep =
  | { type: 'thought'; text: string; agent: string; seq: number; delayMs: number }
  | {
      type: 'tool';
      name: string;
      agent: string;
      seq: number;
      input?: Record<string, unknown>;
      output: string;
      delayStartMs: number;
      delayEndMs: number;
      parentAgent?: string;
    }
  | { type: 'report'; delayMs: number };

/** OpenSRE-style demo: 17 tool calls across planner → domain agents → writeup. */
const DEMO_STEPS: DemoStep[] = [
  {
    type: 'thought',
    agent: 'planner',
    seq: 1,
    text: 'Scoping Pulse alert: high CPU on app-vm-01. Planning parallel evidence collection across Pulse ITSM, metrics, and Log Analytics.',
    delayMs: 350,
  },
  {
    type: 'tool',
    name: 'load_skill',
    agent: 'planner',
    seq: 2,
    input: { skill: 'investigate' },
    output: 'Loaded 5-phase investigation methodology (scope → evidence → hypotheses → test → conclude).',
    delayStartMs: 200,
    delayEndMs: 280,
  },
  {
    type: 'tool',
    name: 'memory.lookup_episodes',
    agent: 'planner',
    seq: 3,
    input: { alert_type: 'HighCPU', limit: 5 },
    output: '2 similar episodes: worker process saturation (resolved), scheduled batch overlap (mitigated).',
    delayStartMs: 200,
    delayEndMs: 300,
  },
  {
    type: 'tool',
    name: 'pulse.get_ticket_context',
    agent: 'pulse',
    seq: 4,
    input: { ticket_number: 'DEMO-CASE-001', include: ['alert', 'engagement', 'resource'] },
    output: 'Ticket DEMO-CASE-001 | alert=HighCPU | engagement=eng-demo | resource=app-vm-01 (Azure VM).',
    delayStartMs: 220,
    delayEndMs: 320,
  },
  {
    type: 'tool',
    name: 'pulse.get_engagement',
    agent: 'pulse',
    seq: 5,
    input: { engagement_id: 'eng-demo' },
    output: 'Azure subscription scoped; Log Analytics workspace law-demo-workspace linked.',
    delayStartMs: 180,
    delayEndMs: 260,
  },
  {
    type: 'tool',
    name: 'pulse.get_related_alerts',
    agent: 'pulse',
    seq: 6,
    input: { engagement_id: 'eng-demo', window: 'PT2H', severity: 'warning,critical' },
    output: '3 related alerts in window; primary HighCPU firing continuously for 28m.',
    delayStartMs: 200,
    delayEndMs: 300,
  },
  {
    type: 'thought',
    agent: 'metrics',
    seq: 7,
    text: 'Statistics first: pull Azure Monitor Percentage CPU and guest process samples before raw logs.',
    delayMs: 300,
  },
  {
    type: 'tool',
    name: 'azure.get_monitor_metrics',
    agent: 'metrics',
    seq: 8,
    input: {
      resource_id:
        '/subscriptions/.../resourceGroups/rg-prod/providers/Microsoft.Compute/virtualMachines/app-vm-01',
      metrics: 'Percentage CPU',
      interval: 'PT5M',
    },
    output: 'Percentage CPU avg=93.4, p95=98.1 over last 30m — threshold breach confirmed.',
    delayStartMs: 250,
    delayEndMs: 450,
  },
  {
    type: 'tool',
    name: 'azure.get_monitor_metrics',
    agent: 'metrics',
    seq: 9,
    input: {
      resource_id:
        '/subscriptions/.../resourceGroups/rg-prod/providers/Microsoft.Compute/virtualMachines/app-vm-01',
      metrics: 'Available Memory Bytes,Disk Read Bytes',
      interval: 'PT5M',
    },
    output: 'Memory pressure moderate; disk I/O normal — CPU-bound pattern.',
    delayStartMs: 200,
    delayEndMs: 350,
  },
  {
    type: 'tool',
    name: 'azure.list_metric_alerts',
    agent: 'metrics',
    seq: 10,
    input: { resource_group: 'rg-prod', filter: 'CPU' },
    output: 'Alert rule cpu-gt-90 matched; action group → Pulse webhook.',
    delayStartMs: 180,
    delayEndMs: 260,
  },
  {
    type: 'thought',
    agent: 'log_analysis',
    seq: 11,
    text: 'Aggregating Perf and Event tables in Log Analytics to identify top CPU consumers.',
    delayMs: 280,
  },
  {
    type: 'tool',
    name: 'azure.query_log_analytics',
    agent: 'log_analysis',
    seq: 12,
    input: {
      workspace_id: 'law-demo-workspace',
      query:
        'Perf | where CounterName == "% Processor Time" | summarize avg(CounterValue) by bin(TimeGenerated, 5m), Computer',
      timespan: 'PT1H',
    },
    output: 'app-vm-01 sustained >90% CPU from 14:02 onward; no other hosts affected.',
    delayStartMs: 280,
    delayEndMs: 520,
  },
  {
    type: 'tool',
    name: 'azure.query_log_analytics',
    agent: 'log_analysis',
    seq: 13,
    input: {
      workspace_id: 'law-demo-workspace',
      query:
        'InsightMetrics | where Name == "Process" | summarize avg(Val) by Tags.Process | top 5 by avg_Val',
      timespan: 'PT30M',
    },
    output: 'Top process: worker.exe (avg 71% CPU); w3wp.exe (12%); System (4%).',
    delayStartMs: 260,
    delayEndMs: 480,
  },
  {
    type: 'tool',
    name: 'azure.query_log_analytics',
    agent: 'log_analysis',
    seq: 14,
    input: {
      workspace_id: 'law-demo-workspace',
      query: 'Event | where EventLevelName == "Error" | summarize count() by bin(TimeGenerated, 10m)',
      timespan: 'PT1H',
    },
    output: 'Error rate elevated but secondary; no OS crash events.',
    delayStartMs: 220,
    delayEndMs: 400,
  },
  {
    type: 'tool',
    name: 'azure.query_resource_graph',
    agent: 'azure',
    seq: 15,
    input: {
      query:
        "Resources | where name =~ 'app-vm-01' | project name, location, properties.hardwareProfile.vmSize, resourceGroup",
    },
    output: 'VM size Standard_D4s_v3, eastus, rg-prod — capacity may be undersized for batch worker.',
    delayStartMs: 240,
    delayEndMs: 380,
  },
  {
    type: 'tool',
    name: 'azure.describe_vm',
    agent: 'azure',
    seq: 16,
    input: { resource_group: 'rg-prod', vm_name: 'app-vm-01' },
    output: 'Power state running; no recent redeploy; extensions healthy.',
    delayStartMs: 220,
    delayEndMs: 340,
  },
  {
    type: 'tool',
    name: 'pulse.search_knowledge',
    agent: 'pulse',
    seq: 17,
    input: { query: 'high CPU worker.exe Azure VM', tags: ['azure', 'cpu'] },
    output: 'Found runbook KB-442: Restart unconstrained worker + review concurrency settings.',
    delayStartMs: 200,
    delayEndMs: 300,
  },
  {
    type: 'thought',
    agent: 'synthesizer',
    seq: 18,
    text: 'Evidence supports probable root cause: unconstrained worker.exe on app-vm-01. Confidence probable; recommend immediate restart and short-term process alerts.',
    delayMs: 320,
  },
  {
    type: 'tool',
    name: 'synthesizer.rank_hypotheses',
    agent: 'synthesizer',
    seq: 19,
    input: {
      hypotheses: [
        'unconstrained worker process',
        'noisy neighbor / host fault',
        'memory leak forcing CPU spin',
      ],
    },
    output: 'H1 unconstrained worker (probable) | H2 host fault (low) | H3 memory leak (unlikely).',
    delayStartMs: 240,
    delayEndMs: 360,
  },
  {
    type: 'tool',
    name: 'writeup.generate_report',
    agent: 'writeup',
    seq: 20,
    input: { schema: 'investigation_v1', include: ['timeline', 'root_cause', 'action_items'] },
    output: 'Structured investigation report generated with executive summary and remediation actions.',
    delayStartMs: 220,
    delayEndMs: 400,
  },
  {
    type: 'tool',
    name: 'memory.store_episode',
    agent: 'writeup',
    seq: 21,
    input: { alert_type: 'HighCPU', resource_type: 'Microsoft.Compute/virtualMachines' },
    output: 'Episode stored for future similar-alert strategy matching.',
    delayStartMs: 180,
    delayEndMs: 260,
  },
  { type: 'report', delayMs: 300 },
];

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const timer = setTimeout(resolve, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/** Local OpenSRE-style demo investigation (~17 tool calls). */
export async function runDemoInvestigationStream(
  handlers: DemoStreamHandlers,
  signal?: AbortSignal
): Promise<void> {
  try {
    for (const step of DEMO_STEPS) {
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

      if (step.type === 'thought') {
        await sleep(step.delayMs, signal);
        handlers.onThought({
          text: step.text,
          ts: new Date().toISOString(),
          seq: step.seq,
          agent: step.agent,
        });
      } else if (step.type === 'tool') {
        await sleep(step.delayStartMs, signal);
        const id = `demo-tool-${step.seq}-${Math.random().toString(36).slice(2, 6)}`;
        handlers.onToolStart({
          id,
          name: step.name,
          agentName: step.agent,
          sequenceNumber: step.seq,
          input: step.input,
          parentAgent: step.parentAgent,
        });
        await sleep(step.delayEndMs, signal);
        handlers.onToolEnd(id, step.output, {
          success: true,
          durationMs: step.delayEndMs,
        });
      } else if (step.type === 'report') {
        await sleep(step.delayMs, signal);
        handlers.onReport(SAMPLE_INVESTIGATION_REPORT, SAMPLE_CONCLUSION_MARKDOWN);
      }
    }
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw err;
    }
    handlers.onError(err instanceof Error ? err.message : 'Demo investigation failed');
  }
}

export async function postInvestigationWorkNote(
  request: PostInvestigationNoteRequest
): Promise<PostInvestigationNoteResult> {
  const { entityType, entityId, markdown } = request;
  if (!entityId?.trim() || !markdown.trim()) {
    return { posted: false, error: 'Missing entity id or note content' };
  }

  const path = workNotePathForEntity(entityType, entityId);
  if (!path) {
    return {
      posted: false,
      error: `Work notes are not supported for entity type "${entityType}" in Phase A`,
    };
  }

  try {
    const { default: api } = await import('../../../services/api');
    const response = await api.post(path, {
      note: markdown,
      isPublic: false,
    });
    return {
      posted: true,
      noteId: response.data?.id || response.data?.workNoteId,
    };
  } catch (err: unknown) {
    const ax = err as {
      response?: { data?: { message?: string; error?: string } };
      message?: string;
    };
    const message =
      ax?.response?.data?.message ||
      ax?.response?.data?.error ||
      ax?.message ||
      'Failed to post work note';
    return { posted: false, error: message };
  }
}

function workNotePathForEntity(entityType: TicketEntityType, entityId: string): string | null {
  switch (entityType) {
    case 'case':
    case 'ticket':
      return `/cases/${encodeURIComponent(entityId)}/work-notes`;
    case 'incident':
      return `/incidents/${encodeURIComponent(entityId)}/work-notes`;
    case 'alert':
      return null;
    default:
      return null;
  }
}

export function buildMarkdownFromReport(report: InvestigationReportData): string {
  return formatReportAsMarkdown(report);
}

export function describeInvestigationMode(): string {
  const mode = getSreInvestigationMode();
  if (mode === 'live') return 'Live SRE Agent';
  if (mode === 'demo') return 'Demo mode (sample Azure investigation)';
  return 'Disabled';
}

/** Helper for consumers that need a typed empty tool list. */
export type { InvestigationToolCall };
